import React from 'react';
import { Upload, Layers, Zap } from 'lucide-react';
export function HowGuardrailsGetBuilt() {
  return <section className="pt-12 pb-24 px-6 md:px-12 lg:px-24 bg-white" data-id="element-564">
      <div className="max-w-7xl mx-auto" data-id="element-565">
        <div className="text-center mb-16" data-id="element-566">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 font-['Syne'] text-black" data-id="element-567">
            How Context Guardrails Are Built
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto" data-id="element-568">
            Security guardrails decide whether AI is allowed to run. Context guardrails decide how it is allowed to think. Those invisible decisions? Synthia governs them by distilling your
            institutional knowledge into machine-readable attributes. Here's how:
          </p>
        </div>

        {/* Step 1 */}
        <div className="mb-20" data-id="element-569">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-570">
            <div data-id="element-571">
              <div className="flex items-center gap-3 mb-4" data-id="element-572">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-573">
                  <Upload className="w-5 h-5 text-[#0066FF]" data-id="element-574" />
                </div>
                <h3 className="text-2xl font-bold font-['Syne'] text-black" data-id="element-575">
                  STEP 1: GUARDRAIL ENRICHMENT
                </h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-576">
                Start with what you already have: compliance documents, brand
                guidelines, SOPs, training materials, expert communications.
                Upload PDFs, paste text, or add URLs. Synthia extracts the
                reasoning patterns, constraints, and decision frameworks
                embedded in your institutional knowledge—turning tacit expertise
                into structured rules.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden" data-id="element-577">
              <img src="https://cdn.magicpatterns.com/uploads/u8VPidG5Wei6F5PX3mwjqM/image_one_enrich.png" alt="Guardrail Enrichment Interface" className="w-[85%] h-auto" data-id="element-578" />
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="mb-20" data-id="element-579">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-580">
            <div data-id="element-581">
              <div className="flex items-center gap-3 mb-4" data-id="element-582">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-583">
                  <Layers className="w-5 h-5 text-green-600" data-id="element-584" />
                </div>
                <h3 className="text-2xl font-bold font-['Syne'] text-black" data-id="element-585">
                  STEP 2: DISTILLATION INTO RULES
                </h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-4" data-id="element-586">
                Your knowledge gets distilled into atomic, composable
                attributes—discrete rules and constraints that can be activated,
                versioned, and controlled. Each attribute is a building block:
              </p>
              <ul className="space-y-2 mb-4" data-id="element-587">
                <li className="text-gray-700 flex items-start gap-2" data-id="element-588">
                  <span className="text-[#0066FF] mt-1" data-id="element-589">•</span>
                  <span data-id="element-590">Activate or deactivate individual rules</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2" data-id="element-591">
                  <span className="text-[#0066FF] mt-1" data-id="element-592">•</span>
                  <span data-id="element-593">Edit definitions without breaking dependencies</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2" data-id="element-594">
                  <span className="text-[#0066FF] mt-1" data-id="element-595">•</span>
                  <span data-id="element-596">Boost priority for critical constraints</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2" data-id="element-597">
                  <span className="text-[#0066FF] mt-1" data-id="element-598">•</span>
                  <span data-id="element-599">Chat with the guardrail to test reasoning</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2" data-id="element-600">
                  <span className="text-[#0066FF] mt-1" data-id="element-601">•</span>
                  <span data-id="element-602">Version and audit every change</span>
                </li>
              </ul>
              <p className="text-gray-600 italic text-sm leading-relaxed" data-id="element-603">
                Example: This "Compliance Officer – Finance" guardrail contains
                20 attributes covering SEC/FINRA requirements, escalation
                protocols, risk thresholds, and disclosure language. This isn't
                a prompt template. It's institutional intelligence as
                infrastructure.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden" data-id="element-604">
              <img src="https://cdn.magicpatterns.com/uploads/eW8x5z98ouU1GHsdKdoBN8/atomic_attributes.png" alt="Atomic Attributes Interface" className="w-[85%] h-auto" data-id="element-605" />
            </div>
          </div>
        </div>

        {/* Step 3 */}
        <div className="mb-16" data-id="element-606">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-607">
            <div data-id="element-608">
              <div className="flex items-center gap-3 mb-4" data-id="element-609">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-610">
                  <Zap className="w-5 h-5 text-purple-600" data-id="element-611" />
                </div>
                <h3 className="text-2xl font-bold font-['Syne'] text-black" data-id="element-612">
                  STEP 3: MCP DEPLOYMENT
                </h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-613">
                Once built, guardrails activate automatically through the Model
                Context Protocol. The right attributes reach the model at the
                right time—perfect situational awareness based on role, intent,
                domain, etc. No manual prompting. No hoping the model remembers.
                Just governed reasoning, every time.
              </p>
            </div>
            <div className="relative bg-gray-50 rounded-xl p-8 border border-gray-200 min-h-[400px] flex items-center justify-center" data-id="element-614">
              {/* Chat interface from MCPSection */}
              <div className="relative w-full max-w-sm" data-id="element-615">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 blur-2xl opacity-20 rounded-full" data-id="element-616"></div>
                <div className="relative bg-white p-6 rounded-lg shadow-xl border border-gray-100" data-id="element-617">
                  <div className="flex items-center gap-3 mb-4 border-b border-gray-100 pb-4" data-id="element-618">
                    <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-619"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-620"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-621"></div>
                    <span className="ml-auto text-xs text-gray-400" data-id="element-622">
                      Claude / ChatGPT / Gemini
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-623">
                    <div className="flex gap-3" data-id="element-624">
                      <div className="w-8 h-8 rounded-full bg-gray-200" data-id="element-625"></div>
                      <div className="flex-1 bg-gray-100 rounded p-2 text-xs text-gray-500" data-id="element-626">
                        Draft a response to the client regarding the merger...
                      </div>
                    </div>

                    {/* Synthia Injection */}
                    <div className="ml-11 p-3 bg-blue-50 rounded border border-blue-100 relative" data-id="element-627">
                      <div className="absolute -left-3 top-3 w-3 h-[1px] bg-blue-200" data-id="element-628"></div>
                      <div className="flex items-center gap-2 mb-2" data-id="element-629">
                        <div className="w-4 h-4 rounded bg-[#0066FF] flex items-center justify-center text-[10px] text-white font-bold" data-id="element-630">
                          S
                        </div>
                        <span className="text-xs font-bold text-[#0066FF]" data-id="element-631">
                          Synthia Active
                        </span>
                      </div>
                      <div className="space-y-1" data-id="element-632">
                        <div className="h-1.5 bg-blue-200 rounded w-3/4" data-id="element-633"></div>
                        <div className="h-1.5 bg-blue-200 rounded w-1/2" data-id="element-634"></div>
                      </div>
                    </div>

                    <div className="flex gap-3 flex-row-reverse" data-id="element-635">
                      <div className="w-8 h-8 rounded-full bg-purple-100" data-id="element-636"></div>
                      <div className="flex-1 bg-white border border-gray-200 rounded p-2 text-xs text-gray-600" data-id="element-637">
                        Based on your corporate communication guidelines (Policy
                        4.2), here is a compliant draft...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="text-center mt-20" data-id="element-638"></div>
      </div>
    </section>;
}